<script setup>
import { computed, watch, onMounted, getCurrentInstance } from 'vue'
import AjaxLoading from './common/AjaxLoading'
import Navigation from './common/navigation/Navigation'
import LoginForm from './common/LoginForm'
import Support from './common/support/Support'
import Modals from './vue/modals/Modals'
import Toasts from "./common/toasts/Toasts"
import UserProfileOffcanvas from "./common/user-profile/UserProfileOffcanvas.vue"

// Context lookup to preserve global Vue instance properties
const { proxy } = getCurrentInstance()
const $store = proxy.$store
const $route = proxy.$route
const $router = proxy.$router

// --- Computed Mappings ---
const user = computed(() => $store.state.user.user)
const userExists = computed(() => $store.state.user.exists)
const tableName = computed(() => $route.params.component)
const componentId = computed(() => $store.state.components.componentIds[tableName.value])
const access = computed(() => $store.getters['user/access'](tableName.value))

// --- Local Methods / Utility Hooks ---
const createFavIconLinkTags = () => {
    let link = document.createElement('link')
    link.setAttribute('rel', 'apple-touch-icon')
    link.setAttribute('href', '/apple-touch-icon.png')
    link.setAttribute('sizes', '180x180')
    document.head.appendChild(link)

    link = document.createElement('link')
    link.setAttribute('rel', 'icon')
    link.setAttribute('type', 'image/png')
    link.setAttribute('href', '/favicon-32x32.png')
    link.setAttribute('sizes', '32x32')
    document.head.appendChild(link)

    link = document.createElement('link')
    link.setAttribute('rel', 'icon')
    link.setAttribute('type', 'image/png')
    link.setAttribute('href', '/favicon-16x16.png')
    link.setAttribute('sizes', '16x16')
    document.head.appendChild(link)
}

const createCKEditorScript = () => {
    const s = document.createElement('script')
    s.setAttribute('src', '/ckeditor/ckeditor.js')
    document.head.appendChild(s)
}

const setRouteProperties = () => {
    let name = $route.name
    let component = $route.params.component
    let id = $route.params.id

    $store.dispatch('stateHistory/set', {
        stateName: name,
        tableName: component,
        id: id,
        fullPath: $route.fullPath
    })

    $store.dispatch('documentTitle/set', {
        stateName: name,
        tableName: component,
        itemId: id,
    })
}

const tokenExists = () => {
    return localStorage.getItem('token')
}

const init = () => {
    let token = localStorage.getItem('token')
    if (token) {
        let userObj
        return $store.dispatch('request/get', {
                url: 'api/users/get-current-user'
            })
            .then((obj) => {
                userObj = obj.data
                return $store.dispatch('request/get', {
                    url: 'api/user/' + userObj.id + '/access'
                })
            })
            .then((obj) => {
                userObj.access = obj.data
                return $store.dispatch('user/set', userObj)
            })
            .then(() => {
                $store.dispatch('user/applyDarkMode')
            })
    }
    return Promise.resolve()
}

const userAccess = () => {
    // Admin only route restriction
    const adminRouteNames = ['themesEditor']
    if (
        $route.name.indexOf(adminRouteNames) > -1
        && !user.value?.administrator
    ) {
        console.error("Access denied! Only administrators have access to the '" + $route.name + "' route.")
        $router.push({ name: 'dashboard' })
        return
    }

    if (
        user.value
        && tableName.value
    ) {
        let id = parseInt($route.params.id)
        let component = $store.state.components.items.find(o => o.id === componentId.value)

        if (
            tableName.value === 'users'
            && id === user.value.id
        ) {
            // Allow users to edit their own user account, even if they haven't access to the users tableName.
        }

        else if (
            user.value.access?.__formAdmin
            && component?.navigationGroup === 'Forms'
        ) {
            // Context placeholder for form access validation logic
        }

        // No access to the component whatsoever
        else if (!access.value) {
            proxy.$nextTick(() => {
                console.log('ss')
                $router.push({ name: 'dashboard' })
            })
        }

        // If an attempt is made to edit or add without permissions then redirect to the listing.
        else if (!!id) {
            let privilege = (id === 0) ? 'add' : 'edit'
            if (!access.value[privilege]) {
                console.log("'" + privilege + "' privilege denied!")
                $router.push({ name: 'list', params: { component: tableName.value } })
            }

        } else {
            // devOnly component restriction.
            if (
                component
                && component.devOnly
                && !$store.state.betaMode
            ) {
                $router.push({ name: 'dashboard' })
            }
        }
    }
}

// --- Lifecycle (Old created() hook phase logic) ---
// Redirect erroneous legacy AngularJS URLs to the dashboard.
if (window.location.href.indexOf('/#/!/') > -1) {
    window.location = window.location.href.split('#')[0]
}

$store.dispatch('setParams')
    .then(() => {
        return init()
    })
    .then(() => {
        userAccess()
    })

createFavIconLinkTags()
createCKEditorScript()
setRouteProperties()

// --- Watch Handlers ---
watch(
    () => $route,
    async (to, from) => {
        await $store.dispatch('modifiedItems/clearModifiedItems')
        setRouteProperties()
        userAccess()
    },
    { deep: true }
)
</script>

<template>
    <div class="d-flex flex-column h-100">
        <div v-if="userExists" class="main-container d-flex flex-column h-100">
            <AjaxLoading/>

            <div class="d-flex h-100 overflow-auto flex-column">
                <Navigation/>

                <div class="main-content flex-grow-1">
                    <router-view/>
                </div>
            </div>

            <Support/>

            <UserProfileOffcanvas/>

            <Modals/>
        </div>
        <div v-else-if="!userExists && tokenExists()" class="ajax-spinner">
            <span class="ajax-spinner-icon"/>
        </div>

        <LoginForm v-else-if="!userExists"/>

        <Toasts/>
    </div>
</template>

<style scoped>
.main-content {
    overflow-y: auto;
}

.ajax-spinner {
    display: flex;
    align-items: center;
    justify-content: center;
    height: 100%;
}
</style>